import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import axios from "axios";
import { API_ORIGIN as API_BASE } from "../../config/api";
import { useAuthStore } from "../../Store/auth";

const FEATURES = [
  "Your coach listing stays public for free",
  "Read and reply to client inquiries",
  "Booking and lead notifications",
  "Keep your profile, gallery, and reviews live",
];

function accessFor(coach) {
  const tierValue = String(coach?.subscriptionTier ?? "0").toLowerCase();
  const tier = tierValue === "basic" ? 1 : ["standard", "pro"].includes(tierValue) ? 2 : Number(tierValue);
  const status = String(coach?.subscriptionStatus ?? "").toLowerCase();
  const active = status === "1" || status === "active";
  const pastDue = status === "2" || status === "pastdue" || status === "past_due";
  const periodEnd = coach?.currentPeriodEndUtc || coach?.subscriptionExpiresAtUtc || null;
  const futurePeriod = periodEnd && new Date(periodEnd).getTime() > Date.now();
  const paid = Boolean(coach?.stripeSubscriptionId);
  return {
    free: !paid && active && tier > 0 && futurePeriod,
    existingSubscription: paid && (active || pastDue),
    pastDue,
    cancelScheduled: Boolean(coach?.cancelAtPeriodEnd),
    periodEnd,
  };
}

export default function Subscription() {
  const { token, coachId } = useAuthStore();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [yearly, setYearly] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);
  const accountRef = useRef("");
  const currentAccount = `${coachId || ""}:${token || ""}`;
  accountRef.current = currentAccount;

  const reloadCoach = async () => {
    if (!coachId || !token) {
      setCoach(null);
      setLoading(false);
      return false;
    }
    const account = `${coachId}:${token}`;
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/Coaches/${coachId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (accountRef.current !== account) return false;
      setCoach(response.data);
      setError("");
      return true;
    } catch {
      if (accountRef.current === account) setError("Could not refresh your subscription. Please retry.");
      return false;
    } finally {
      if (accountRef.current === account) setLoading(false);
    }
  };

  useEffect(() => {
    setCoach(null);
    setError("");
    void reloadCoach();
    // Account identity is the intended refresh boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount]);

  const access = accessFor(coach);
  const canCheckout = Boolean(coach && !error && !access.free && !access.existingSubscription && !busy);

  const startCheckout = async () => {
    if (!canCheckout || busyRef.current || !coachId || !token) return;
    busyRef.current = true;
    setBusy("checkout");
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/Billing/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ coachId: Number(coachId), plan: "lead_access", yearly }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) throw new Error(data?.message || "Checkout is unavailable. Please retry.");
      window.location.assign(data.url);
    } catch (failure) {
      setError(failure.message || "Checkout is unavailable. Please retry.");
      setBusy("");
      busyRef.current = false;
    }
  };

  const cancel = async () => {
    if (!access.existingSubscription || access.cancelScheduled || busyRef.current || !coachId || !token) return;
    if (!window.confirm("Cancel paid access? Your listing stays public and lead access continues through the current billing period.")) return;
    busyRef.current = true;
    setBusy("cancel");
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/Billing/subscription/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ coachId: Number(coachId) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Cancellation could not be confirmed.");
      await reloadCoach();
    } catch (failure) {
      setError(failure.message || "Cancellation could not be confirmed.");
    } finally {
      setBusy("");
      busyRef.current = false;
    }
  };

  const date = access.periodEnd ? new Date(access.periodEnd).toLocaleDateString() : null;
  const statusText = access.pastDue
    ? "Payment is overdue. Resolve your existing subscription before starting another checkout."
    : access.cancelScheduled
      ? `Paid access ends ${date || "at the end of your billing period"}. Your free listing stays live.`
      : access.existingSubscription
        ? `Your lead access is active${date ? ` through ${date}` : ""}.`
        : access.free
          ? `Your early access is active${date ? ` through ${date}` : ""}.`
          : "Your coach listing stays public for free. Subscribe when you want to read and answer client inquiries.";

  return (
    <section className="w-full bg-gradient-to-b from-blue-50 to-white px-4 py-16">
      <div className="mx-auto max-w-3xl">
        <p className="text-center text-xs font-bold uppercase tracking-widest text-blue-600">PTFinderNow for coaches</p>
        <h1 className="mt-3 text-center text-3xl font-bold text-slate-900 md:text-4xl">One plan for client leads</h1>
        <p className="mt-3 text-center text-slate-600">Stay discoverable for free. Pay only when you want access to client inquiries.</p>

        {!token || !coachId ? (
          <div className="mt-8 rounded-2xl border border-blue-100 bg-white p-6 text-center">
            <p className="text-slate-700">Sign in as a coach to manage your access.</p>
            <a href="/login" className="mt-4 inline-block font-semibold text-blue-600">Sign in</a>
          </div>
        ) : loading ? (
          <p className="mt-8 text-center text-slate-600">Loading your subscription...</p>
        ) : error && !coach ? (
          <button onClick={reloadCoach} className="mt-8 w-full rounded-2xl border border-red-200 bg-white p-5 text-red-700">{error} Tap to retry.</button>
        ) : (
          <>
            <div className="mt-8 rounded-2xl border border-blue-100 bg-white p-5 text-center text-slate-700">{statusText}</div>
            <div className="mt-6 rounded-3xl border border-blue-200 bg-white p-7 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Lead Access</p>
              <div className="mt-5 grid grid-cols-2 gap-3" aria-label="Billing period">
                <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)} className={`rounded-xl border p-3 text-left ${!yearly ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white"}`}>
                  <span className="block text-sm font-bold text-slate-900">Monthly</span>
                  <span className="block text-xs text-slate-600">AED 199 / month</span>
                </button>
                <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)} className={`rounded-xl border p-3 text-left ${yearly ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white"}`}>
                  <span className="block text-sm font-bold text-slate-900">Yearly · save 20%</span>
                  <span className="block text-xs text-slate-600">AED 1,910.00 / year</span>
                </button>
              </div>
              <div className="mt-4 flex items-baseline gap-2"><strong className="text-4xl text-slate-900">AED {yearly ? "1,910.00" : "199"}</strong><span className="text-slate-500">/ {yearly ? "year" : "month"}</span></div>
              {yearly && <p className="mt-1 text-sm font-semibold text-emerald-700">AED 159.17/month equivalent · Save AED 478.00 each year</p>}
              <p className="mt-3 text-sm text-slate-600">Billed {yearly ? "yearly" : "monthly"} from today. No free trial. Automatically renews until canceled; your free listing remains visible.</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-800">
                {FEATURES.map((feature) => <li key={feature} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-blue-600" /><span>{feature}</span></li>)}
              </ul>
              <button onClick={startCheckout} disabled={!canCheckout} className="mt-7 w-full rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                {busy === "checkout" ? "Opening checkout..." : access.existingSubscription ? "Current paid access" : access.free ? "Available after early access" : yearly ? "Unlock leads · yearly" : "Unlock leads · monthly"}
              </button>
            </div>
            {access.existingSubscription && !access.cancelScheduled && <button onClick={cancel} disabled={!!busy} className="mx-auto mt-5 block text-sm font-semibold text-blue-700 disabled:opacity-50">{busy === "cancel" ? "Cancelling..." : "Cancel paid access"}</button>}
            {error && <p role="alert" className="mt-4 text-center text-sm text-red-700">{error}</p>}
          </>
        )}
      </div>
    </section>
  );
}
